/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { SupportContact } from "./support.type";
import { replyToSupportMessage } from "../../../services/firebaseService";

export const SupportApi = createApi({
  reducerPath: "supportApi",
  tagTypes: ["support", "supports"],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getSupportContacts: builder.query({
      //
      queryFn: async () => {
        // Note : Pense à vérifier si ta collection s'appelle "supports" ou "support" au pluriel/singulier
        const supportsRef = collection(db, "supports");
        const q = query(supportsRef, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);

        const supports = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const support = docSnap.data();

            // 1. Récupérer les infos de l'expediteur (créateur du ticket)
            if (support.createdBy) {
              const userRef = doc(db, "users", support.createdBy);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                support.user = { uid: userSnap.id, ...userSnap.data() };
              }
            }

            // 2. Parcourir le tableau de réponses déjà imbriqué pour hydrater l'admin
            const rawAnswers = support.answers || [];
            support.answers = await Promise.all(
              rawAnswers.map(async (answer: any) => {
                if (answer.answeredBy) {
                  const authorRef = doc(db, "users", answer.answeredBy);
                  const authorSnap = await getDoc(authorRef);
                  if (authorSnap.exists()) {
                    answer.author = { uid: authorSnap.id, ...authorSnap.data() };
                  }
                }
                return answer;
              }),
            );

            return { id: docSnap.id, ...support };
          }),
        );

        return { data: supports as SupportContact[] };
      },

      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;
          const supportsRef = collection(db, "supports");
          const q = query(supportsRef, orderBy("createdAt", "desc"));

          unsubscribe = onSnapshot(q, async (snapshot) => {
            const supports = await Promise.all(
              snapshot.docs.map(async (docSnap) => {
                const support = docSnap.data();

                // 1. Récupérer les infos du professeur
                if (support.createdBy) {
                  const userRef = doc(db, "users", support.createdBy);
                  const userSnap = await getDoc(userRef);
                  if (userSnap.exists()) {
                    support.user = { uid: userSnap.id, ...userSnap.data() };
                  }
                }

                // 2. Hydrater les auteurs des réponses imbriquées
                const rawAnswers = support.answers || [];
                support.answers = await Promise.all(
                  rawAnswers.map(async (answer: any) => {
                    if (answer.answeredBy) {
                      const authorRef = doc(db, "users", answer.answeredBy);
                      const authorSnap = await getDoc(authorRef);
                      if (authorSnap.exists()) {
                        answer.author = {
                          uid: authorSnap.id,
                          ...authorSnap.data(),
                        };
                      }
                    }
                    return answer;
                  }),
                );

                return { id: docSnap.id, ...support };
              }),
            );

            updateCachedData(() => supports as SupportContact[]);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["supports"],
    }),
    getSupportContactsByUser: builder.query({
      queryFn: async ({ userId, status }) => {
        const supportsRef = collection(db, "supports");
        let q = query(
          supportsRef,
          where("createdBy", "==", userId),
          orderBy("createdAt", "desc"),
        );

        if (status) {
          q = query(
            supportsRef,
            where("createdBy", "==", userId),
            where("status", "==", status),
            orderBy("createdAt", "desc"),
          );
        }

        const snapshot = await getDocs(q);

        const supports = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const support = docSnap.data();

            // 1. Récupérer les infos de l'utilisateur (créateur du ticket)
            if (support.createdBy) {
              const userRef = doc(db, "users", support.createdBy);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                support.user = { uid: userSnap.id, ...userSnap.data() };
              }
            }

            // 2. Hydrater les auteurs des réponses imbriquées
            const rawAnswers = support.answers || [];
            support.answers = await Promise.all(
              rawAnswers.map(async (answer: any) => {
                if (answer.answeredBy) {
                  const authorRef = doc(db, "users", answer.answeredBy);
                  const authorSnap = await getDoc(authorRef);
                  if (authorSnap.exists()) {
                    answer.author = { uid: authorSnap.id, ...authorSnap.data() };
                  }
                }
                return answer;
              }),
            );

            return { id: docSnap.id, ...support };
          }),
        );

        return { data: supports as SupportContact[] };
      },

      async onCacheEntryAdded(
        { userId, status },
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;
          const supportsRef = collection(db, "supports");
          let q = query(
            supportsRef,
            where("createdBy", "==", userId),
            orderBy("createdAt", "desc"),
          );
          if (status) {
            q = query(
              supportsRef,
              where("createdBy", "==", userId),
              where("status", "==", status),
              orderBy("createdAt", "desc"),
            );
          }

          unsubscribe = onSnapshot(q, async (snapshot) => {
            const supports = await Promise.all(
              snapshot.docs.map(async (docSnap) => {
                const support = docSnap.data();

                if (support.createdBy) {
                  const userRef = doc(db, "users", support.createdBy);
                  const userSnap = await getDoc(userRef);
                  if (userSnap.exists()) {
                    support.user = { uid: userSnap.id, ...userSnap.data() };
                  }
                }

                const rawAnswers = support.answers || [];
                support.answers = await Promise.all(
                  rawAnswers.map(async (answer: any) => {
                    if (answer.answeredBy) {
                      const authorRef = doc(db, "users", answer.answeredBy);
                      const authorSnap = await getDoc(authorRef);
                      if (authorSnap.exists()) {
                        answer.author = {
                          uid: authorSnap.id,
                          ...authorSnap.data(),
                        };
                      }
                    }
                    return answer;
                  }),
                );

                return { id: docSnap.id, ...support };
              }),
            );

            updateCachedData(() => supports as SupportContact[]);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["supports"],
    }),
    createSupportMessage: builder.mutation({
      async queryFn(data) {
        try {
          const user = auth.currentUser;
          if (!user) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }
          const userId = user?.uid;
          const docRef = await addDoc(collection(db, "supports"), {
            ...data,
            createdAt: serverTimestamp(),
            createdBy: userId,
            updatedAt: serverTimestamp(),
          });
          return {
            data: {
              id: docRef.id,
              message:
                "Merci pour votre message ! Votre demande sera traitée dans les plus brefs délais",
              ...data,
            },
          };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["support", "supports"],
    }),
    replyToSupport: builder.mutation({
      async queryFn(dataForm) {
        try {
          const user = auth.currentUser;
          if (!user) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }
          const data = await replyToSupportMessage(dataForm);
          return {
            data: {
              ...(data as any),
              message: "Réponse envoyée.",
            },
          };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["support", "supports"],
    }),
    toggleSupportStatus: builder.mutation({
      queryFn: async ({ id, autoRenew }) => {
        try {
          const docRef = doc(db, "supports", id);
          await updateDoc(docRef, { autoRenew, updatedAt: serverTimestamp() });
          return {
            data: {
              message: `Le status changé.`,
              success: true,
            },
          };
        } catch (error: any) {
          return { error: { status: "TOGGLE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["support", "supports"],
    }),
    archiveSupport: builder.mutation({
      queryFn: async ({ id, archived }: { id: string; archived: boolean }) => {
        try {
          const docRef = doc(db, "supports", id);
          await updateDoc(docRef, { archived, updatedAt: serverTimestamp() });
          return {
            data: {
              message: archived ? "Ticket archivé." : "Ticket désarchivé.",
              success: true,
            },
          };
        } catch (error: any) {
          return { error: { status: "ARCHIVE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["support", "supports"],
    }),
    // Supprimer
    deleteSupport: builder.mutation({
      queryFn: async (uid) => {
        try {
          const docRef = doc(db, "supports", uid);
          await deleteDoc(docRef);
          return {
            data: {
              uid,
              message: "Message contact supprimé.",
              success: true,
            },
          };
        } catch (error: any) {
          return { error: { status: "DELETE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["support", "supports"],
    }),
  }),
});

export const {
  useGetSupportContactsQuery,
  useGetSupportContactsByUserQuery,
  useCreateSupportMessageMutation,
  useDeleteSupportMutation,
  useToggleSupportStatusMutation,
  useReplyToSupportMutation,
  useArchiveSupportMutation,
} = SupportApi;
